/**
 * Brand seam (Decision #21). The white-label brand a customer sees is resolved
 * from the request host / their business's reseller. Owner + staff + POS chrome
 * wrap their subtree with this so every "Possignin"/"itcitysoft.com" literal that
 * has been migrated onto useBrand() renders the reseller's identity instead.
 *
 * Mock: the active customer (Khan Bakery) is an Acme reseller customer, so we
 * derive the brand from `currentReseller` (Acme POS). Real impl: the API returns
 * the resolved brand in the session/bootstrap payload keyed by hostname.
 */
import type { Brand } from '../brand/BrandProvider';
import { PLATFORM_BRAND, useBrand } from '../brand/BrandProvider';
import { USE_MOCKS, apiGet } from './client';
import { currentReseller } from '../mocks/reseller';

/** Acme's customer-facing brand, derived from the reseller's branding config. */
export const activeBrand: Brand = {
  name: currentReseller.displayName,
  shortName: currentReseller.name.split(' ')[0] ?? currentReseller.displayName,
  primaryColor: currentReseller.primaryColor,
  supportEmail: currentReseller.supportEmail,
  supportPhone: currentReseller.supportPhone,
  logoUrl: currentReseller.logoUrl,
  faviconUrl: null,
  // The reseller's own apex (not a *.itcitysoft.com subdomain) → fully white-label.
  domain:
    currentReseller.domains.find((d) => !d.hostname.endsWith('itcitysoft.com'))?.hostname ??
    PLATFORM_BRAND.domain,
  // A reseller surface — never the platform.
  isPlatform: false,
  allowedDbModes: ['shared', 'managed', 'self_hosted'],
};

/**
 * The brand for the current customer surface. Defaults to the platform brand
 * when mocks are off (real resolution happens server-side from the host).
 */
export function useActiveBrand(): Brand {
  // The authenticated shells (AppShell/StaffShell) re-wrap their subtree with this.
  // In real mode the white-label brand is resolved per-host at startup
  // (main.tsx → fetchHostBrand) and lives in the BrandProvider context, so reuse
  // it. Returning PLATFORM_BRAND here overrode the host brand on every
  // authenticated surface (tab title + accent colour) — leaking "Possignin"/indigo
  // on a reseller's logged-in app. Mocks keep the static reseller brand.
  const hostBrand = useBrand();
  return USE_MOCKS ? activeBrand : hostBrand;
}

/** Shape of GET /api/v1/brand?host=<host> (public, pre-auth). */
interface HostBrandResponse {
  name: string;
  shortName: string;
  primaryColor: string;
  supportEmail: string;
  supportPhone: string | null;
  logoUrl: string | null;
  faviconUrl?: string | null;
  isPlatform: boolean;
  domain: string;
  allowedDbModes?: string[];
}

/**
 * Resolve the white-label brand for the CURRENT host (Decision #21). In real
 * mode this calls the public `/brand?host=<host>` endpoint (no auth needed) and
 * maps it onto the Brand type used by the BrandProvider; any failure falls back
 * to the platform brand so a transient API error never leaks a wrong identity or
 * blocks first paint. In mock mode it returns the existing reseller mock brand.
 *
 * The API's `isPlatform` flag rides through as `Brand.isPlatform` so the router
 * can pick the reseller Landing vs. the ITCity Company page per host.
 */
export async function fetchHostBrand(): Promise<Brand> {
  if (USE_MOCKS) return activeBrand;
  try {
    const host = window.location.host;
    const res = await apiGet<HostBrandResponse>(`/brand?host=${encodeURIComponent(host)}`);
    return {
      name: res.name,
      shortName: res.shortName,
      primaryColor: res.primaryColor,
      supportEmail: res.supportEmail,
      supportPhone: res.supportPhone,
      logoUrl: res.logoUrl,
      faviconUrl: res.faviconUrl ?? null,
      domain: res.domain,
      isPlatform: res.isPlatform,
      allowedDbModes: res.allowedDbModes ?? ['shared'],
    };
  } catch {
    return PLATFORM_BRAND;
  }
}
